/**
 * An article's versions, newest first, with the change between any two.
 *
 * The automation publishes without review, so "what changed, who changed it and
 * why" has to be readable afterwards — that is what this is for, rather than
 * convenience. Each entry compares against the one below it, which is the
 * question someone actually has: what did this version do?
 *
 * The diff is computed here rather than fetched because the history already
 * carries every body; asking the server for something it just sent would be a
 * round trip for arithmetic.
 */

import { LoadingSpinner } from "@/components/common/LoadingSpinner/LoadingSpinner"
import { Typography } from "@/components/common/Typography/Typography"
import { Button } from "@/components/ui/button"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { IconArrowLeft, IconRobot } from "@tabler/icons-react"
import { useMemo, useState } from "react"
import { useHelpArticleHistory } from "../api/helpAdminApi"
import type { HelpVersion } from "../api/types"
import { AUTOMATION_AUTHOR, diffLines } from "../history"

function Line({ kind, text }: { kind: "added" | "removed"; text: string }) {
  return (
    <div
      className={cn(
        "font-mono whitespace-pre-wrap wrap-break-word px-2 py-0.5 text-[12px]",
        kind === "added"
          ? "bg-success/10 text-success"
          : "bg-red1/10 text-red1/80",
      )}
    >
      <span className="select-none opacity-60">
        {kind === "added" ? "+ " : "- "}
      </span>
      {text || " "}
    </div>
  )
}

function Entry({
  version,
  previous,
  lang,
}: {
  version: HelpVersion
  previous?: HelpVersion
  lang: string
}) {
  const { translate } = useTranslation()
  const [open, setOpen] = useState(false)

  const changes = useMemo(
    () => diffLines(previous?.bodyMarkdown ?? "", version.bodyMarkdown),
    [previous?.bodyMarkdown, version.bodyMarkdown],
  )
  const byAutomation = version.createdBy === AUTOMATION_AUTHOR
  const stamp = version.publishedAt ?? version.updatedAt

  return (
    <li className="rounded-lg border border-white/6">
      <div className="flex flex-wrap items-center gap-2 px-M py-2.5">
        <span className="font-mono text-[12px] text-white/70">
          v{version.versionNo}
        </span>
        <span
          className={cn(
            "rounded-full border px-2 py-0.5 text-[11px]",
            version.status === "published"
              ? "border-yellow1/30 text-yellow1"
              : "border-white/10 text-white/40",
          )}
        >
          {translate(
            version.status === "published"
              ? "HELPDESK/HISTORY_LIVE"
              : version.status === "draft"
                ? "HELPDESK/MANAGE_UNWRITTEN"
                : "HELPDESK/HISTORY_SUPERSEDED",
          )}
        </span>

        <Typography as="span" variant="textXS" className="text-white/70">
          {version.title}
        </Typography>

        <span className="ml-auto flex items-center gap-2 text-white/40">
          {byAutomation ? <IconRobot size={13} /> : null}
          <Typography as="span" variant="textXS">
            {version.createdBy ?? "—"}
          </Typography>
          {stamp ? (
            <Typography as="span" variant="textXS">
              {new Date(stamp).toLocaleDateString(lang)}
            </Typography>
          ) : null}
          <Button
            size="sm"
            variant="ghost"
            className="text-grey5 hover:text-white"
            onClick={() => setOpen((value) => !value)}
          >
            {translate(
              open ? "HELPDESK/HISTORY_HIDE" : "HELPDESK/HISTORY_SHOW",
            )}
          </Button>
        </span>
      </div>

      {version.changeNote ? (
        <Typography
          as="p"
          variant="textXS"
          className="px-M pb-2.5 text-white/45"
        >
          {version.changeNote}
        </Typography>
      ) : null}

      {open ? (
        <div className="border-t border-white/6 py-1">
          {changes.length === 0 ? (
            <Typography
              as="p"
              variant="textXS"
              className="px-M py-2 text-white/40"
            >
              {translate("HELPDESK/HISTORY_NO_TEXT_CHANGE")}
            </Typography>
          ) : (
            changes.map((change, index) => (
              <Line
                key={`${change.kind}-${index}`}
                kind={change.kind}
                text={change.text}
              />
            ))
          )}
        </div>
      ) : null}
    </li>
  )
}

export function HelpdeskArticleHistory({
  articleId,
  lang,
  onClose,
}: {
  articleId: string
  lang: string
  onClose: () => void
}) {
  const { translate } = useTranslation()
  const history = useHelpArticleHistory(articleId, lang, true)

  return (
    <div className="h-full min-w-0 overflow-y-auto bg-grey1 px-M py-L lg:px-XXL lg:py-XL">
      <div className="mx-auto max-w-4xl">
        <button
          type="button"
          onClick={onClose}
          className="mb-M flex items-center gap-1.5 text-grey5 hover:text-white"
        >
          <IconArrowLeft size={14} />
          <Typography as="span" variant="textXS">
            {translate("HELPDESK/MANAGE_TITLE")}
          </Typography>
        </button>

        <Typography as="h1" variant="titleM" className="pb-1">
          {translate("HELPDESK/HISTORY_TITLE")}
        </Typography>
        <Typography as="p" variant="textS" className="pb-L text-grey5">
          {translate("HELPDESK/HISTORY_SUBTITLE", {
            article: articleId,
            lang: lang.toUpperCase(),
          })}
        </Typography>

        {history.isLoading ? (
          <div className="flex justify-center py-XL">
            <LoadingSpinner />
          </div>
        ) : history.isError || !history.data ? (
          <Typography as="p" variant="textS" className="text-white/60">
            {translate("HELPDESK/HISTORY_ERROR")}
          </Typography>
        ) : history.data.versions.length === 0 ? (
          <Typography as="p" variant="textS" className="text-white/60">
            {translate("HELPDESK/HISTORY_EMPTY")}
          </Typography>
        ) : (
          <ul className="flex flex-col gap-2">
            {history.data.versions.map((version, index) => (
              <Entry
                key={`${version.lang}-${version.versionNo}`}
                version={version}
                previous={history.data.versions[index + 1]}
                lang={lang}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
